import { useCallback, useEffect, useMemo, useState } from 'react';
import { HandCoins, Plus, ShieldCheck, Trash2, Loader2, AlertTriangle } from 'lucide-react';
import {
  Card,
  Button,
  StatusBadge,
  type StatusTone,
  Modal,
  ModalBody,
  ModalFooter,
  Input,
  FormField,
  NotificationCard,
  useToast,
} from '@sighthost/ui';
import {
  paymentArrangementService,
  type ArrangementRow,
  type InstallmentRow,
  type ArrangementStatus,
  type InstallmentStatus,
} from '@/domains/billing/services/paymentArrangementService';
import { useStaffRole } from '@/shared/hooks/useStaffRole';
import { formatZAR } from '@/shared/utils/format';

/**
 * PaymentArrangementPanel — agree, view and cancel a client payment
 * arrangement from the customer's Billing tab.
 *
 * WHY IT LIVES HERE rather than on its own tab: an arrangement is a collections
 * decision, read alongside the invoices it was struck over. A sixth tab would
 * also push the customer record to the two-level navigation cap (§4.1) for a
 * surface used a handful of times a month.
 *
 * Read is open to every staff member — whoever takes the client's call needs to
 * see that an agreement exists. Write is admin only, matching the RLS policy
 * and the Edge Functions; the form simply isn't rendered for anyone else.
 */

const STATUS_TONE: Record<ArrangementStatus, StatusTone> = {
  active: 'active',
  completed: 'paid',
  broken: 'failed',
  cancelled: 'draft',
};

const STATUS_LABEL: Record<ArrangementStatus, string> = {
  active: 'Holding dunning',
  completed: 'Kept',
  broken: 'Broken',
  cancelled: 'Cancelled',
};

const INSTALLMENT_TONE: Record<InstallmentStatus, StatusTone> = {
  pending: 'pending',
  met: 'paid',
  missed: 'failed',
};

/** Today in SAST (UTC+2, no DST) — CLAUDE.md §4.14. */
function sastToday(): string {
  return new Date(Date.now() + 2 * 3600_000).toISOString().slice(0, 10);
}

function addDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

interface DraftInstallment {
  due_date: string;
  amount: string;
}

interface Props {
  customerId: string;
  customerName: string;
  /** Open invoice total, used only to seed a sensible first instalment. */
  outstanding?: number;
}

export function PaymentArrangementPanel({ customerId, customerName, outstanding }: Props) {
  const { toast } = useToast();
  const role = useStaffRole();
  const canWrite = role.isAdmin;

  const [rows, setRows] = useState<ArrangementRow[]>([]);
  const [installments, setInstallments] = useState<Record<string, InstallmentRow[]>>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<ArrangementRow | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const list = await paymentArrangementService.listForCustomer(customerId);
      setRows(list);
      const detail: Record<string, InstallmentRow[]> = {};
      await Promise.all(
        list.map(async (a) => {
          detail[a.id] = await paymentArrangementService.listInstallments(a.id);
        }),
      );
      setInstallments(detail);
    } catch (err) {
      // Surfaced, never swallowed — a silently empty panel would read as
      // "no arrangement exists", which is the opposite of the truth and could
      // get a client suspended mid-agreement.
      setRows([]);
      setLoadError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => { void load(); }, [load]);

  const active = useMemo(() => rows.find((r) => r.status === 'active'), [rows]);

  return (
    <Card className="space-y-4 p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <HandCoins className="h-4 w-4 text-muted-foreground" />
            Payment arrangements
          </h3>
          <p className="mt-1 max-w-prose text-xs text-muted-foreground">
            An arrangement pauses grace, suspension and termination — and the emails that go
            with them — until the date you set. Renewal invoices keep being raised, because the
            debt is still owed. It breaks by itself the morning after a promised payment is missed.
          </p>
        </div>
        {canWrite && !active && (
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" /> Agree an arrangement
          </Button>
        )}
      </div>

      {active && (
        <div className="flex items-start gap-2 rounded border border-success/30 bg-success/5 p-3 text-sm">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-success" />
          <span className="text-foreground">
            Dunning is held for {customerName} until{' '}
            <strong className="tabular-nums">{active.holds_until}</strong>. Suspension and
            termination will be refused by the database while this arrangement is active.
          </span>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading arrangements…</p>
      ) : loadError ? (
        <div className="rounded border border-danger/30 bg-danger/5 p-3 text-sm">
          <p className="font-medium text-danger">Could not load arrangements.</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Do not assume there is none — treat this as unknown until it loads. Error:{' '}
            <span className="font-mono">{loadError}</span>
          </p>
          <Button type="button" variant="outline" size="sm" className="mt-2" onClick={() => void load()}>
            Retry
          </Button>
        </div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No arrangement has been agreed with this customer. Normal dunning applies.
        </p>
      ) : (
        <ul className="space-y-3">
          {rows.map((a) => (
            <li key={a.id} className="rounded border border-border p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs text-foreground">{a.number}</span>
                  <StatusBadge tone={STATUS_TONE[a.status]}>{STATUS_LABEL[a.status]}</StatusBadge>
                </div>
                {canWrite && a.status === 'active' && (
                  <Button size="sm" variant="outline" onClick={() => setCancelTarget(a)}>
                    <Trash2 className="h-4 w-4" /> Cancel
                  </Button>
                )}
              </div>

              <p className="mt-2 text-sm text-foreground">{a.reason}</p>

              <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground sm:grid-cols-4">
                <div><dt className="inline">Holds until </dt><dd className="inline tabular-nums text-foreground">{a.holds_until}</dd></div>
                <div><dt className="inline">Promised </dt><dd className="inline tabular-nums text-foreground">{formatZAR(Number(a.promised_total))}</dd></div>
                <div><dt className="inline">Kept </dt><dd className="inline tabular-nums text-foreground">{a.installments_met} of {a.installments_total}</dd></div>
                <div><dt className="inline">Agreed by </dt><dd className="inline text-foreground">{a.agreed_by_name ?? a.agreed_by_email ?? '—'}</dd></div>
              </dl>

              {(installments[a.id] ?? []).length > 0 && (
                <ul className="mt-2 divide-y divide-border border-t border-border text-xs">
                  {(installments[a.id] ?? []).map((it) => (
                    <li key={it.id} className="flex items-center justify-between gap-3 py-1.5">
                      <span className="tabular-nums text-muted-foreground">{it.due_date}</span>
                      <span className="tabular-nums font-medium text-foreground">{formatZAR(Number(it.amount))}</span>
                      <StatusBadge tone={INSTALLMENT_TONE[it.status]}>{it.status}</StatusBadge>
                    </li>
                  ))}
                </ul>
              )}

              {a.status === 'broken' && a.broken_reason && (
                <p className="mt-2 flex items-start gap-1.5 text-xs text-danger">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  {a.broken_reason} Dunning has resumed.
                </p>
              )}
              {a.status === 'cancelled' && (
                <p className="mt-2 text-xs text-muted-foreground">
                  Cancelled{a.cancel_reason ? ` — ${a.cancel_reason}` : ''}. Dunning has resumed.
                </p>
              )}
            </li>
          ))}
        </ul>
      )}

      {createOpen && (
        <CreateArrangementModal
          customerId={customerId}
          customerName={customerName}
          outstanding={outstanding}
          onClose={() => setCreateOpen(false)}
          onDone={() => { setCreateOpen(false); void load(); }}
        />
      )}

      {cancelTarget && (
        <CancelArrangementModal
          arrangement={cancelTarget}
          onClose={() => setCancelTarget(null)}
          onDone={() => { setCancelTarget(null); void load(); }}
        />
      )}
    </Card>
  );

  function CancelArrangementModal({
    arrangement, onClose, onDone,
  }: { arrangement: ArrangementRow; onClose: () => void; onDone: () => void }) {
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    async function submit() {
      setBusy(true); setErr(null);
      try {
        await paymentArrangementService.cancel(arrangement.id, reason.trim() || undefined);
        toast({ tone: 'success', title: 'Arrangement cancelled', body: `${arrangement.number} ended. Dunning resumes.` });
        onDone();
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e));
      } finally { setBusy(false); }
    }

    return (
      <Modal open onOpenChange={(o) => !o && onClose()} title={`Cancel ${arrangement.number}?`}>
        <ModalBody className="space-y-3">
          <NotificationCard
            tone="warning"
            title="Collection pressure resumes immediately"
            body="The next billing-cron run may move this customer's invoices to grace or suspended and suspend their cPanel account. If you only want the hold to end naturally, close this — it lapses on its own."
          />
          <FormField label="Why (optional, recorded against the arrangement)" htmlFor="cancel_reason">
            <Input id="cancel_reason" value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Client withdrew from the agreement" maxLength={500} disabled={busy} />
          </FormField>
          {err && <p className="text-sm text-danger">{err}</p>}
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Keep it</Button>
          <Button onClick={() => void submit()} disabled={busy} loading={busy}>
            {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Cancelling…</> : 'Cancel arrangement'}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }

  function CreateArrangementModal({
    customerId: cid, customerName: cname, outstanding: owed, onClose, onDone,
  }: {
    customerId: string; customerName: string; outstanding?: number;
    onClose: () => void; onDone: () => void;
  }) {
    const today = sastToday();
    const [reason, setReason] = useState('');
    const [holdsUntil, setHoldsUntil] = useState(addDays(today, 30));
    const [draft, setDraft] = useState<DraftInstallment[]>([
      { due_date: addDays(today, 14), amount: owed && owed > 0 ? owed.toFixed(2) : '' },
    ]);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    const promised = draft.reduce((s, d) => s + (parseFloat(d.amount) || 0), 0);

    const validation = useMemo(() => {
      if (reason.trim().length < 8) return 'Say why the arrangement was agreed (at least 8 characters).';
      if (holdsUntil < today) return 'The hold must end in the future.';
      if (draft.length === 0) return 'Add at least one promised payment.';
      for (const d of draft) {
        if (!d.due_date) return 'Every promised payment needs a date.';
        const amt = parseFloat(d.amount);
        if (!isFinite(amt) || amt <= 0) return 'Every promised payment needs an amount above zero.';
        if (d.due_date > holdsUntil) return `A payment is promised for ${d.due_date}, after the hold ends on ${holdsUntil}.`;
      }
      return null;
    }, [reason, holdsUntil, draft, today]);

    async function submit() {
      if (validation) { setErr(validation); return; }
      setBusy(true); setErr(null);
      try {
        const res = await paymentArrangementService.create({
          customer_id: cid,
          reason: reason.trim(),
          holds_until: holdsUntil,
          installments: draft.map((d) => ({ due_date: d.due_date, amount: parseFloat(d.amount) })),
        });
        toast({
          tone: 'success',
          title: `Arrangement ${res.number} recorded`,
          body: `Dunning held for ${cname} until ${res.holds_until}.`,
        });
        onDone();
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e));
      } finally { setBusy(false); }
    }

    return (
      <Modal open onOpenChange={(o) => !o && onClose()} size="wide"
        title={`Agree a payment arrangement with ${cname}`}>
        <ModalBody className="space-y-4">
          <FormField label="Why was this agreed?" htmlFor="arr_reason" required
            helper="Recorded permanently and visible to all staff. Name the circumstance and who you spoke to.">
            <Input id="arr_reason" value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Cash-flow; spoke to the owner 31 Aug, agreed to settle over two payments"
              maxLength={1000} disabled={busy} />
          </FormField>

          <FormField label="Hold dunning until" htmlFor="arr_holds"
            helper="No suspension, termination or dunning email before this date. Must be on or after the last promised payment.">
            <Input id="arr_holds" type="date" value={holdsUntil} min={today}
              onChange={(e) => setHoldsUntil(e.target.value)} disabled={busy} />
          </FormField>

          <div className="space-y-2">
            <p className="text-sm font-medium text-foreground">What the client promised</p>
            {draft.map((d, i) => (
              <div key={i} className="flex items-end gap-2">
                <FormField label={i === 0 ? 'Date' : ''} htmlFor={`d_${i}`} className="flex-1">
                  <Input id={`d_${i}`} type="date" value={d.due_date} min={today} disabled={busy}
                    onChange={(e) => setDraft((p) => p.map((x, j) => j === i ? { ...x, due_date: e.target.value } : x))} />
                </FormField>
                <FormField label={i === 0 ? 'Amount (R)' : ''} htmlFor={`a_${i}`} className="flex-1">
                  <Input id={`a_${i}`} inputMode="decimal" value={d.amount} placeholder="129.00" disabled={busy}
                    onChange={(e) => setDraft((p) => p.map((x, j) => j === i ? { ...x, amount: e.target.value } : x))} />
                </FormField>
                <Button type="button" variant="outline" size="sm" disabled={busy || draft.length === 1}
                  onClick={() => setDraft((p) => p.filter((_, j) => j !== i))}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" disabled={busy || draft.length >= 24}
              onClick={() => setDraft((p) => [...p, { due_date: addDays(holdsUntil, 0), amount: '' }])}>
              <Plus className="h-4 w-4" /> Add a payment
            </Button>
            <p className="text-xs text-muted-foreground">
              Total promised: <strong className="tabular-nums text-foreground">{formatZAR(promised)}</strong>
              {typeof owed === 'number' && owed > 0 && (
                <> · currently outstanding <span className="tabular-nums">{formatZAR(owed)}</span></>
              )}
            </p>
          </div>

          <NotificationCard
            tone="info"
            title="This does not restore a suspended site"
            body="If the cPanel account is already suspended, lift it from the Hosting tab as a separate step. Filing an arrangement stops things getting worse; it does not put the customer back online."
          />

          {err && <p className="text-sm text-danger">{err}</p>}
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={() => void submit()} disabled={busy || !!validation} loading={busy}>
            {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Recording…</> : 'Record arrangement'}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }
}
